/** Tarif de la prestation FYT d'un partenaire + palier de gratuité (en centimes). */
export type PartnerPricing = {
  /** Tarif TTC de la prestation, en centimes. */
  prestationCents: number;
  /** Achats (centimes) à partir desquels la prestation est offerte ; null = jamais offerte. */
  freeThresholdCents: number | null;
};

/** Tarif appliqué tant qu'un partenaire n'a pas de réglage propre : 50 €, jamais offerte. */
export const DEFAULT_PARTNER_PRICING: PartnerPricing = {
  prestationCents: 5000,
  freeThresholdCents: null,
};

/** Convertit les colonnes `partner` en tarif ; toute valeur invalide retombe sur le défaut. */
export function pricingFromRow(
  row: { prestation_cents?: number | null; free_threshold_cents?: number | null } | null | undefined,
): PartnerPricing {
  const p = row?.prestation_cents;
  const t = row?.free_threshold_cents;
  return {
    prestationCents:
      typeof p === "number" && Number.isInteger(p) && p >= 0
        ? p
        : DEFAULT_PARTNER_PRICING.prestationCents,
    freeThresholdCents: typeof t === "number" && Number.isInteger(t) && t > 0 ? t : null,
  };
}

export type PricingInputResult =
  | { valid: true; pricing: PartnerPricing }
  | { valid: false; reason: string };

/**
 * Valide la saisie back-office (montants en euros, décimales acceptées).
 * Tarif vide → défaut 50 € ; palier vide → pas de palier (jamais offerte).
 */
export function parsePricingInput(input: {
  prestationEuros?: number | string | null;
  freeThresholdEuros?: number | string | null;
}): PricingInputResult {
  const toCents = (v: number | string | null | undefined): number | null | "invalid" => {
    if (v === null || v === undefined) return null;
    const s = String(v).trim().replace(",", ".");
    if (s === "") return null;
    const n = Number(s);
    if (!Number.isFinite(n) || n < 0) return "invalid";
    return Math.round(n * 100);
  };
  const prestation = toCents(input.prestationEuros);
  if (prestation === "invalid") return { valid: false, reason: "Tarif de la prestation invalide." };
  const threshold = toCents(input.freeThresholdEuros);
  if (threshold === "invalid") return { valid: false, reason: "Palier invalide." };
  if (threshold === 0) return { valid: false, reason: "Le palier doit être supérieur à 0 € (laisser vide = jamais offerte)." };
  return {
    valid: true,
    pricing: {
      prestationCents: prestation ?? DEFAULT_PARTNER_PRICING.prestationCents,
      freeThresholdCents: threshold,
    },
  };
}

/** Centimes → saisie en euros pour un champ de formulaire ("50", "12,5") ; null → champ vide. */
export function centsToEuroInput(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return "";
  const euros = cents / 100;
  return Number.isInteger(euros) ? String(euros) : String(euros).replace(".", ",");
}
